import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import {
  CheckCircleOutline,
  ArrowBackOutlined,
  CreditCardOutlined,
  ManageAccountsOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';
import stripeService from '../services/stripeService';

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, subscriptionStatus, refreshSubscriptionStatus } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Charger le statut d'abonnement au montage du composant
    refreshSubscriptionStatus();

    // Vérifier s'il y a un message de 402 en attente
    const subscriptionMessage = sessionStorage.getItem('subscription_required_message');
    if (subscriptionMessage) {
      setError(subscriptionMessage);
      sessionStorage.removeItem('subscription_required_message');
    }
  }, [refreshSubscriptionStatus]);

  const handleSubscribe = async () => {
    // Rediriger vers la nouvelle page Pricing
    navigate('/pricing');
  };

  const handleManageSubscription = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Créer un lien vers le portail client Stripe
      const portalUrl = await stripeService.createCustomerPortal();

      // Rediriger vers le portail client Stripe
      window.location.href = portalUrl;
    } catch (err: any) {
      console.error('Erreur lors de l\'accès au portail client:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'accès au portail client');
      setIsLoading(false);
    }
  };

  const benefits = [
    { text: 'Devis illimités', icon: '📄' },
    { text: 'Factures illimitées', icon: '💰' },
    { text: 'Calculateurs métiers', icon: '🧮' },
    { text: 'Signature électronique', icon: '✍️' },
    { text: 'Gestion des acomptes', icon: '💵' },
    { text: 'Photos de chantier', icon: '📸' },
    { text: 'Génération PDF automatique', icon: '📑' },
    { text: 'Support prioritaire', icon: '🎯' },
  ];

  const isSubscribed = subscriptionStatus?.subscriptionActive || false;
  const isInTrial = subscriptionStatus?.isInTrialPeriod || false;
  const hasActiveAccess = subscriptionStatus?.hasActiveAccess || false;
  const daysRemaining = subscriptionStatus?.subscriptionDaysRemaining;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          Abonnement
        </Typography>
        <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Retour
        </Button>
      </Box>

      {/* Message d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statut actuel */}
      {hasActiveAccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {isInTrial ? (
            <>
              <strong>Période d'essai active !</strong> Vous avez accès à toutes les fonctionnalités.
              {daysRemaining !== null && daysRemaining !== undefined && daysRemaining >= 0 && (
                <> Il vous reste <strong>{daysRemaining} jours</strong> d'essai gratuit.</>
              )}
            </>
          ) : (
            <>
              <strong>Abonnement actif !</strong> Vous avez un accès complet à toutes les fonctionnalités.
            </>
          )}
        </Alert>
      )}

      {/* Section de gestion pour les abonnés */}
      {isSubscribed && (
        <Paper
          elevation={6}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(145deg, #10B981 0%, #059669 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                ✅ Vous êtes abonné
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Gérez votre abonnement, changez de plan ou mettez à jour vos informations de paiement
              </Typography>
              {subscriptionStatus?.subscriptionTier && (
                <Chip
                  label={`Plan ${subscriptionStatus.subscriptionTier === 'PREMIUM' ? 'Premium' : 'Essentiel'}`}
                  sx={{
                    mt: 2,
                    bgcolor: 'white',
                    color: '#10B981',
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleManageSubscription}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <ManageAccountsOutlined />}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 700,
                bgcolor: 'white',
                color: '#10B981',
                '&:hover': {
                  bgcolor: '#F9FAFB',
                },
              }}
            >
              {isLoading ? 'Chargement...' : 'Gérer mon abonnement'}
            </Button>
          </Box>
        </Paper>
      )}

      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        {/* Titre général */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#374151',
              mb: 2,
            }}
          >
            Accès illimité à tous nos outils
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#6B7280',
            }}
          >
            Gérez vos devis, factures et chantiers en toute simplicité
          </Typography>
        </Box>

        {/* Information sur les offres */}
        {!isSubscribed && (
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: 3,
              mb: 4,
              textAlign: 'center',
              background: 'linear-gradient(145deg, #FEF3C7 0%, #FDE68A 100%)',
              border: '2px solid #F59E0B',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#92400E', mb: 2 }}>
              Découvrez nos offres Essentiel et Premium
            </Typography>
            <Typography variant="body1" sx={{ color: '#92400E', mb: 3 }}>
              Choisissez l'offre qui correspond à vos besoins et profitez de fonctionnalités avancées
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                bgcolor: '#F59E0B',
                '&:hover': {
                  bgcolor: '#D97706',
                },
              }}
            >
              Voir les offres
            </Button>
          </Paper>
        )}

        {/* Carte commune avec avantages */}
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #F9FAFB 100%)',
            border: '2px solid #E5E7EB',
          }}
        >
          <Typography variant="body2" sx={{ color: '#6B7280', textAlign: 'center', mb: 3 }}>
            30 jours d'essai gratuit inclus • Sans engagement
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Liste des avantages */}
          <List sx={{ mb: 4 }}>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#F9FAFB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.3rem',
                    }}
                  >
                    {benefit.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={benefit.text}
                  primaryTypographyProps={{
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                />
                <CheckCircleOutline sx={{ color: '#10B981', fontSize: 28 }} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 4 }} />

          {/* Boutons d'action */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!isSubscribed ? (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSubscribe}
                  startIcon={<CreditCardOutlined />}
                  sx={{
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    bgcolor: '#F59E0B',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#D97706',
                    },
                  }}
                >
                  Découvrir nos offres
                </Button>
                <Typography variant="body2" align="center" sx={{ color: '#374151', fontWeight: 600, mt: 2 }}>
                  Offres Essentiel et Premium disponibles
                </Typography>
              </>
            ) : (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleManageSubscription}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ManageAccountsOutlined />}
                sx={{
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  bgcolor: '#374151',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1F2937',
                  },
                }}
              >
                {isLoading ? 'Chargement...' : 'Gérer mon abonnement'}
              </Button>
            )}

            {isInTrial && !isSubscribed && (
              <Typography variant="caption" align="center" sx={{ color: '#6B7280', mt: 1 }}>
                Vous pouvez souscrire à tout moment pendant votre période d'essai
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Informations supplémentaires */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Paiement sécurisé par Stripe • Annulation possible à tout moment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Des questions ? Contactez-nous à support@sersal.fr
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SubscriptionPage;
